/**
 * Prototype store for the Leagues feature.
 *
 * Everything lives client-side: state is seeded from `prototypeData`, kept in a
 * module singleton, persisted to localStorage and published through
 * `useSyncExternalStore`. A timer fakes the realtime settlement stream so the
 * league table moves while you watch it.
 *
 * When this becomes real, swap the action bodies for API calls and the settlement
 * timer for the existing settlement sockets — component code should not change.
 */

import {
    LEAGUE_BOT_PLAY_CHANCE,
    LEAGUE_BOT_PLAY_SETTLE_MAX_MS,
    LEAGUE_BOT_PLAY_SETTLE_MIN_MS,
    LEAGUE_GAME_KEYS,
    LEAGUE_GAME_META,
    LEAGUE_MAX_OPEN_BOT_PLAYS,
    LEAGUE_PLAY_SETTLE_DELAY_MS,
    LEAGUE_PLAY_STAKE_MIN,
    LEAGUE_PLAY_STAKE_PRESETS,
    LEAGUE_SIMULATION_TICK_MS,
    LEAGUE_VIEWER_USER_ID,
    PRIZE_COINS_PER_RUBY,
    SEAT_TOPUP_RUBIES,
    seatTopUpMax,
} from "./constants";
import {
    formatCoins,
    isLeagueRefunded,
    leagueFormatOvers,
    leagueIncludedGames,
    leagueSeatTopUpAvailableAtOrBelow,
    leaguePrizePotCoins,
    leaguePrizePotCoinsForSeats,
    leagueStatus,
    leagueTableRows,
    memberSeatTopups,
    seatChipsCountingTowardTopUp,
    seatTopUpChips,
    startingCoinsFor,
} from "./leagueFormat";
import {
    LEAGUE_BULLION_BATTLE_STAKE,
    leagueBullionBattleName,
} from "./leagueGameSurfaces";
import { resolveLeagueBullionViewerSlip } from "./leagueBullionLobbyPicks";
import { allPlayOptions, availableCoinsFor } from "./leaguePlays";
import {
    applyLeagueReactionToMembers,
    leagueReactionActivityFromThrow,
    prependLeagueReactionEvent,
} from "./leagueReactionStats";
import {
    buildBullionDemoFilledBoard,
    buildBullionDemoRoom,
} from "../../Bullion/lib/bullionDemoMatchFixtures";
import { persistSixesDemoMatchFinished } from "../../Sixes/demo/sixesDemoFinish";
import { friendById, newMember, prototypeFixtures, prototypeLeagues, buildWalkthroughLiveLeague, WALKTHROUGH_LIVE_LEAGUE_ID, PRESENTER_PREMATCH_MS } from "./prototypeData";
import type {
    CreateLeagueInput,
    League,
    LeagueBullionBattle,
    LeagueGameKey,
    LeagueMember,
    LeaguePlay,
    LeagueSettlement,
    LeaguesState,
} from "./types";

const STORAGE_KEY = "c8_leagues_prototype_v12";

type PersistedState = LeaguesState & { seededAt: number };

const listeners = new Set<() => void>();
/** Wall-clock when the current seed was written — must not reset on every tick/persist. */
let seededAtMs = Date.now();
let state: LeaguesState = loadState();
let timerId: ReturnType<typeof setInterval> | null = null;

function seedState(): LeaguesState {
    seededAtMs = Date.now();
    return { leagues: prototypeLeagues(), vip: true, simulating: false };
}

/**
 * Seed data is written relative to "now", so after a reload every timestamp is
 * replayed forward by however long the tab was closed. That keeps live leagues
 * live and the activity feed fresh without re-seeding away the user's own leagues.
 */
function shiftTimestamps(input: LeaguesState, driftMs: number): LeaguesState {
    if (!Number.isFinite(driftMs) || driftMs <= 0) return input;
    const shift = (iso: string): string => {
        const at = new Date(iso).getTime();
        return Number.isFinite(at) ? new Date(at + driftMs).toISOString() : iso;
    };
    return {
        ...input,
        leagues: input.leagues.map((league) => ({
            ...league,
            createdAt: shift(league.createdAt),
            startsAt: shift(league.startsAt),
            endsAt: shift(league.endsAt),
            fixture: league.fixture ? { ...league.fixture, start: shift(league.fixture.start) } : null,
            members: league.members.map((member) => ({
                ...member,
                joinedAt: shift(member.joinedAt),
                rankMovedAt: member.rankMovedAt ? shift(member.rankMovedAt) : undefined,
            })),
            settlements: league.settlements.map((row) => ({ ...row, at: shift(row.at) })),
            seatTopUps: (league.seatTopUps ?? []).map((row) => ({ ...row, at: shift(row.at) })),
            reactionEvents: (league.reactionEvents ?? []).map((row) => ({ ...row, at: shift(row.at) })),
            plays: (league.plays ?? []).map((row) => ({
                ...row,
                placedAt: shift(row.placedAt),
                settlesAt: row.settlesAt ? shift(row.settlesAt) : row.settlesAt,
                settledAt: row.settledAt ? shift(row.settledAt) : row.settledAt,
            })),
        })),
    };
}

/** Previous seat rates. Rebase persisted demo leagues to the current format-aware rate. */
const LEGACY_CHIPS_PER_RUBY = [1000, 3000, 5000, 10000] as const;

function normalizeLeague(league: League): League {
    const included = league.includedGames?.filter((game) => LEAGUE_GAME_KEYS.includes(game)) ?? [...LEAGUE_GAME_KEYS];
    const newStarting = startingCoinsFor(league.entryFeeRubies, leagueFormatOvers(league));
    const fee = Math.max(0, Math.round(league.entryFeeRubies));
    return {
        ...league,
        includedGames: included.length > 0 ? included : [...LEAGUE_GAME_KEYS],
        plays: league.plays ?? [],
        seatTopUps: league.seatTopUps ?? [],
        reactionEvents: league.reactionEvents ?? [],
        members: league.members.map((member) => {
            const legacyRate = LEGACY_CHIPS_PER_RUBY.find((rate) => member.startingCoins === fee * rate);
            if (legacyRate == null) return member;
            const oldStarting = fee * legacyRate;
            if (oldStarting === newStarting) return member;
            return {
                ...member,
                startingCoins: newStarting,
                coins: Math.max(0, member.coins + (newStarting - oldStarting)),
            };
        }),
    };
}

/**
 * Presenter India–WI must stay upcoming until Start match. Older localStorage
 * (or a closed tab) can let kickoff age into the past — only then rewind it.
 * A still-future kickoff is kept so the lobby and game pages share one clock.
 */
function ensurePresenterPrematch(input: LeaguesState, now: number = Date.now()): LeaguesState {
    const idx = input.leagues.findIndex((league) => league.id === WALKTHROUGH_LIVE_LEAGUE_ID);
    if (idx < 0) {
        return { ...input, leagues: [buildWalkthroughLiveLeague(now), ...input.leagues] };
    }
    const league = input.leagues[idx]!;
    if (league.presenterMatchStarted || league.settledAt) return input;
    const currentStart = new Date(league.fixture?.start || league.startsAt).getTime();
    if (Number.isFinite(currentStart) && currentStart > now) return input;
    const startMs = now + PRESENTER_PREMATCH_MS;
    const durationHours = league.fixture?.durationHours ?? 5;
    const next: League = {
        ...league,
        startsAt: new Date(startMs).toISOString(),
        endsAt: new Date(startMs + durationHours * 3600000).toISOString(),
        fixture: league.fixture
            ? { ...league.fixture, start: new Date(startMs).toISOString() }
            : league.fixture,
        presenterMatchStarted: false,
    };
    const leagues = input.leagues.map((row, i) => (i === idx ? next : row));
    return { ...input, leagues };
}

function loadState(): LeaguesState {
    if (typeof window === "undefined") return seedState();
    try {
        const raw = window.localStorage.getItem(STORAGE_KEY);
        if (!raw) return seedState();
        const parsed = JSON.parse(raw) as PersistedState;
        if (!parsed || !Array.isArray(parsed.leagues) || parsed.leagues.length === 0) return seedState();
        seededAtMs = Number(parsed.seededAt ?? Date.now());
        if (!Number.isFinite(seededAtMs)) seededAtMs = Date.now();
        const shifted = shiftTimestamps(
            {
                leagues: parsed.leagues.map(normalizeLeague),
                vip: parsed.vip !== false,
                simulating: parsed.simulating === true,
            },
            Date.now() - seededAtMs,
        );
        return ensurePresenterPrematch(shifted);
    } catch {
        return seedState();
    }
}

function persist(): void {
    if (typeof window === "undefined") return;
    try {
        const payload: PersistedState = { ...state, seededAt: seededAtMs };
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    } catch {
        /* Private mode / quota — the prototype still works in memory. */
    }
}

function commit(next: LeaguesState): void {
    state = next;
    persist();
    listeners.forEach((listener) => listener());
    syncTimer();
}

function updateLeague(leagueId: string, updater: (league: League) => League): void {
    let changed = false;
    const leagues = state.leagues.map((league) => {
        if (league.id !== leagueId) return league;
        changed = true;
        return updater(league);
    });
    if (!changed) return;
    commit({ ...state, leagues });
}

/** Demo-only local toggle for league start reminders. */
export function patchLeagueRemindMePrototype(leagueId: string, remindMe: boolean): void {
    updateLeague(leagueId, (league) => ({ ...league, remindMe: Boolean(remindMe) }));
}

export function getLeaguesSnapshot(): LeaguesState {
    return state;
}

export function subscribeToLeagues(listener: () => void): () => void {
    listeners.add(listener);
    syncTimer();
    // Settle already-due seed plays on first subscriber without waiting a full tick.
    if (listeners.size === 1 && state.simulating) {
        queueMicrotask(() => tick());
    }
    return () => {
        listeners.delete(listener);
        syncTimer();
    };
}

export function findLeague(leagueId: string | undefined): League | null {
    if (!leagueId) return null;
    return state.leagues.find((league) => league.id === leagueId) ?? null;
}

/** Shared prematch kickoff for `/leaguesdemo` lobby, Sixes, Bullion, Trader, and Squares. */
export function presenterDemoKickoffIso(leagueId?: string | null, matchId?: string | null): string | null {
    const fromLeague = leagueId ? findLeague(leagueId) : null;
    const needle = String(matchId || "").trim();
    const fromMatch = needle
        ? state.leagues.find((row) => String(row.fixture?.id || "").trim() === needle) ?? null
        : null;
    const league = fromLeague ?? fromMatch;
    if (!league) return null;
    const iso = String(league.fixture?.start || league.startsAt || "").trim();
    return iso || null;
}

export function findLeagueByCode(code: string | undefined): League | null {
    const needle = String(code ?? "").trim().toUpperCase();
    if (!needle) return null;
    return state.leagues.find((league) => league.code.toUpperCase() === needle) ?? null;
}

/** Leagues the viewer hosts or has joined. */
export function myLeagues(): League[] {
    return state.leagues.filter((league) => league.membership === "host" || league.membership === "member");
}

export function pendingInvites(): League[] {
    return state.leagues.filter((league) => league.membership === "invited");
}

/** The first league the viewer is in (as host or member) for a given fixture, if any. */
export function viewerLeagueForFixture(
    fixtureId: string | null | undefined,
    excludeLeagueId?: string,
): League | null {
    if (!fixtureId) return null;
    return (
        state.leagues.find(
            (league) =>
                league.id !== excludeLeagueId &&
                (league.membership === "host" || league.membership === "member") &&
                league.fixture?.id === fixtureId,
        ) ?? null
    );
}

export function setVip(vip: boolean): void {
    commit({ ...state, vip });
}

export function setSimulating(simulating: boolean): void {
    commit({ ...state, simulating });
}

export function resetLeaguesPrototype(): void {
    clearPresenterSixesReveal();
    commit(seedState());
}

function clearPresenterSixesReveal(): void {
    if (typeof window === "undefined") return;
    const matchId = "proto-ind-wi";
    try {
        window.sessionStorage.removeItem(`sixes:card-revealed:${matchId}`);
        window.sessionStorage.removeItem(`sixes:simulation-complete:${matchId}`);
        window.sessionStorage.removeItem(`sixes:demo-buckets:${matchId}`);
        window.localStorage.removeItem(`sixes:settlement-seen:${matchId}`);
        window.sessionStorage.removeItem(`bullion:league-sim-complete:${WALKTHROUGH_LIVE_LEAGUE_ID}`);
    } catch {
        /* ignore */
    }
}

function clearPresenterSixesSettlement(): void {
    if (typeof window === "undefined") return;
    const matchId = "proto-ind-wi";
    try {
        window.sessionStorage.removeItem(`sixes:simulation-complete:${matchId}`);
        window.localStorage.removeItem(`sixes:settlement-seen:${matchId}`);
        window.sessionStorage.removeItem(`bullion:league-sim-complete:${WALKTHROUGH_LIVE_LEAGUE_ID}`);
    } catch {
        /* ignore */
    }
}

function markPresenterBullionSimComplete(leagueId: string): void {
    if (typeof window === "undefined") return;
    const id = String(leagueId || "").trim();
    if (!id) return;
    try {
        window.sessionStorage.setItem(`bullion:league-sim-complete:${id}`, "1");
        window.dispatchEvent(new CustomEvent("c8:bullion-league-sim-complete", { detail: { leagueId: id } }));
    } catch {
        /* ignore */
    }
}

/**
 * If the viewer skipped Bullion entry / Trader before kickoff, fill a lucky-dip slip
 * and a few open trades so My Games is not an empty Closed / No trades board.
 */
function seedPresenterKickoffViewerBoards(leagueId: string): void {
    const league = findLeague(leagueId);
    if (!league) return;
    const viewer = league.members.find((member) => member.isViewer);
    if (!viewer) return;

    if (leagueIncludedGames(league).includes("bullion")) {
        const slip = resolveLeagueBullionViewerSlip(league, viewer.userId);
        const picks = slip?.picks;
        const hasPicks = Boolean(picks && typeof picks === "object" && Object.keys(picks).length > 0);
        if (!hasPicks) {
            const filled = buildBullionDemoFilledBoard(buildBullionDemoRoom(league.fixture?.start));
            if (filled) {
                enterLeagueBullionBattle(leagueId, {
                    userId: viewer.userId,
                    playerName: viewer.playerName,
                    selections: filled.selections as Record<string, unknown>,
                    bonusPicks: filled.bonusPicks,
                });
            }
        }
    }

    const latest = findLeague(leagueId) ?? league;
    if (!leagueIncludedGames(latest).includes("trader")) return;
    if (latest.plays.some((play) => play.game === "trader" && play.userId === viewer.userId)) return;

    const home = latest.fixture?.homeTeam?.trim() || "India";
    const away = latest.fixture?.awayTeam?.trim() || "West Indies";
    const trades: Array<{
        stake: number;
        selection: string;
        multiplier: number;
        marketDetails: LeaguePlay["marketDetails"];
    }> = [
        {
            stake: 200,
            selection: `${home} @ 1.72`,
            multiplier: 1.72,
            marketDetails: { marketName: "Match Winner", selectionName: home, marketClass: "Win", marketTeam: home },
        },
        {
            stake: 150,
            selection: `Over 165.5 @ 1.88`,
            multiplier: 1.88,
            marketDetails: {
                marketName: `${home} Innings Runs`,
                selectionName: "Over",
                marketClass: "Line",
                marketLine: "165.5",
                marketTeam: home,
            },
        },
        {
            stake: 100,
            selection: `${away} Most Sixes @ 2.02`,
            multiplier: 2.02,
            marketDetails: {
                marketName: "Most Sixes",
                selectionName: away,
                marketClass: "Win",
                marketTeam: away,
            },
        },
    ];
    for (const trade of trades) {
        placePlay(leagueId, {
            userId: viewer.userId,
            playerName: viewer.playerName,
            game: "trader",
            stake: trade.stake,
            selection: trade.selection,
            multiplier: trade.multiplier,
            marketDetails: trade.marketDetails,
        });
    }
}

/** Flip the presenter India–WI fixture to live and start light peer-play juice. */
export function startPresenterMatch(leagueId: string = WALKTHROUGH_LIVE_LEAGUE_ID): void {
    clearPresenterSixesSettlement();
    const now = Date.now();
    updateLeague(leagueId, (league) => {
        const durationHours = league.fixture?.durationHours ?? 5;
        return {
            ...league,
            presenterMatchStarted: true,
            startsAt: new Date(now).toISOString(),
            endsAt: new Date(now + durationHours * 3600000).toISOString(),
            fixture: league.fixture ? { ...league.fixture, start: new Date(now).toISOString() } : league.fixture,
        };
    });
    seedPresenterKickoffViewerBoards(leagueId);
    commit({ ...state, simulating: true });
}

function presenterDemoScore(userId: number, salt: number): number {
    return 40 + ((Math.abs(userId) * 17 + salt) % 9) * 20;
}

function presenterTraderWon(play: LeaguePlay): boolean {
    const hay = `${play.marketDetails?.selectionName ?? ""} ${play.selection}`.toLowerCase();
    if (hay.includes("west indies") || hay.includes("away")) return false;
    return hay.includes("india") || hay.includes("over") || hay.includes("home");
}

function settlePresenterOpenPlay(league: League, play: LeaguePlay): League {
    if (play.status !== "open") return league;
    if (play.game === "sixes") {
        const existing = Math.max(0, Math.round(Number(play.marketDetails?.cardScore) || 0));
        const cardScore = existing > 0 ? existing : presenterDemoScore(play.userId, 3);
        return settlePlayWithReturn(league, play.id, cardScore, {
            cardScore,
            cardScoreUpdatedAt: new Date().toISOString(),
        });
    }
    if (play.game === "bullion") {
        const existing = Math.max(0, Math.round(Number(play.marketDetails?.settlement?.points) || 0));
        const slip = existing > 0 ? existing : presenterDemoScore(play.userId, 11);
        return settlePlayWithReturn(league, play.id, slip, {
            settlement: {
                points: slip,
                potentialPoints: slip,
                settledMarkets: 5,
                totalMarkets: 5,
                allSettled: true,
                slipScore: slip,
                placeBonus: 0,
            },
        });
    }
    if (play.game === "squares") {
        const scored = Math.max(0, Math.round(Number(play.marketDetails?.superSquares?.returnCoins) || 0));
        const returned = scored > 0 ? scored : Math.max(0, Math.round(play.stake * 0.45));
        return settlePlayWithReturn(league, play.id, returned);
    }
    const won = presenterTraderWon(play);
    const returned = won ? Math.round(play.stake * play.multiplier) : 0;
    return settlePlayWithReturn(league, play.id, returned);
}

/**
 * Settle every open play, freeze the boards, and close the presenter league so the podium draws.
 */
export function finishPresenterLeague(leagueId: string = WALKTHROUGH_LIVE_LEAGUE_ID): void {
    const current = findLeague(leagueId);
    if (!current || current.settledAt) return;
    const matchId = String(current.fixture?.id || "").trim() || "proto-ind-wi";
    persistSixesDemoMatchFinished(matchId);
    markPresenterBullionSimComplete(leagueId);
    const now = Date.now();
    const iso = new Date(now).toISOString();
    updateLeague(leagueId, (league) => {
        if (league.settledAt) return league;
        let next = league;
        for (const play of league.plays.filter((row) => row.status === "open")) {
            next = settlePresenterOpenPlay(next, play);
        }
        next = {
            ...next,
            plays: next.plays.map((play) => {
                if (play.game === "sixes") {
                    const score = Math.max(0, Math.round(Number(play.marketDetails?.cardScore) || 0));
                    if (score > 0) return play;
                    const cardScore = presenterDemoScore(play.userId, 3);
                    return {
                        ...play,
                        marketDetails: {
                            ...(play.marketDetails ?? {}),
                            cardScore,
                            cardScoreUpdatedAt: iso,
                        },
                    };
                }
                if (play.game === "bullion" && play.marketDetails?.settlement?.points == null) {
                    const slip = presenterDemoScore(play.userId, 11);
                    return {
                        ...play,
                        marketDetails: {
                            ...(play.marketDetails ?? {}),
                            settlement: {
                                points: slip,
                                potentialPoints: slip,
                                settledMarkets: 5,
                                totalMarkets: 5,
                                allSettled: true,
                                slipScore: slip,
                                placeBonus: 0,
                            },
                        },
                    };
                }
                return play;
            }),
        };
        return {
            ...next,
            presenterMatchStarted: true,
            startsAt: league.presenterMatchStarted ? league.startsAt : iso,
            endsAt: iso,
            settledAt: iso,
            status: "ended",
            fixture: next.fixture
                ? { ...next.fixture, start: league.presenterMatchStarted ? next.fixture.start : iso }
                : next.fixture,
            bullionBattle: next.bullionBattle
                ? { ...next.bullionBattle, status: "settled", settledAt: iso }
                : next.bullionBattle,
        };
    });
    if (state.simulating) commit({ ...state, simulating: false });
}

/**
 * Re-seeds the walkthrough league (kept for Tools / accidental callers).
 */
export function replayLiveLeagueDemo(leagueId: string | undefined): void {
    if (leagueId !== WALKTHROUGH_LIVE_LEAGUE_ID) return;
    resetLeaguesPrototype();
}

function makeCode(name: string): string {
    const letters = name
        .toUpperCase()
        .replace(/[^A-Z0-9]/g, "")
        .slice(0, 4)
        .padEnd(3, "X");
    let code = "";
    for (let attempt = 0; attempt < 50; attempt += 1) {
        const suffix = Math.floor(100 + Math.random() * 900);
        code = `${letters}${suffix}`;
        if (!findLeagueByCode(code)) return code;
    }
    return code;
}

export function createLeague(
    input: CreateLeagueInput,
    viewer: { name: string; avatar: string | null; xpWeekCrown?: string | null },
): League {
    const now = Date.now();
    const fixture =
        input.fixture ??
        (input.fixtureId ? prototypeFixtures(now).find((f) => f.id === input.fixtureId) ?? null : null);
    const startsAt = input.scope === "match" && fixture ? fixture.start : new Date(now).toISOString();
    const endsAt =
        input.scope === "match" && fixture
            ? new Date(new Date(fixture.start).getTime() + fixture.durationHours * 3600000).toISOString()
            : new Date(input.endsAt ?? now + 86400000).toISOString();

    const includedGames = (() => {
        const picked = input.includedGames?.filter((game) => LEAGUE_GAME_KEYS.includes(game)) ?? [...LEAGUE_GAME_KEYS];
        return picked.length > 0 ? picked : [...LEAGUE_GAME_KEYS];
    })();

    const league: League = {
        id: `league-${now}-${Math.floor(Math.random() * 1000)}`,
        code: makeCode(input.name),
        name: input.name.trim(),
        scope: input.scope,
        entryFeeRubies: input.entryFeeRubies,
        prizePotCoins: leaguePrizePotCoinsForSeats(input.entryFeeRubies, 1),
        createdAt: new Date(now).toISOString(),
        startsAt,
        endsAt,
        fixture: input.scope === "match" ? fixture : null,
        includedGames,
        hostUserId: LEAGUE_VIEWER_USER_ID,
        hostName: viewer.name,
        members: [
            newMember({
                userId: LEAGUE_VIEWER_USER_ID,
                playerName: viewer.name,
                avatar: viewer.avatar,
                xpWeekCrown: viewer.xpWeekCrown ?? null,
                isHost: true,
                isViewer: true,
                entryFeeRubies: input.entryFeeRubies,
                formatOvers: input.scope === "match" ? fixture?.formatOvers : null,
                joinedAt: new Date(now).toISOString(),
            }),
        ],
        invitedUserIds: [],
        membership: "host",
        settlements: [],
        seatTopUps: [],
        reactionEvents: [],
        plays: [],
        bullionBattle:
            input.scope === "match" && includedGames.includes("bullion")
                ? {
                      name: leagueBullionBattleName({ name: input.name.trim() }),
                      entryStake: LEAGUE_BULLION_BATTLE_STAKE,
                      ensuredAt: new Date(now).toISOString(),
                  }
                : undefined,
    };
    commit({ ...state, leagues: [league, ...state.leagues] });
    return league;
}

/**
 * Idempotent: ensure a match league with Bullion has a shared prototype private battle.
 * Returns the battle meta (existing or newly written).
 */
export function ensureLeagueBullionBattle(leagueId: string): LeagueBullionBattle | null {
    const existing = findLeague(leagueId);
    if (!existing) return null;
    if (existing.scope !== "match" || !leagueIncludedGames(existing).includes("bullion")) {
        return existing.bullionBattle ?? null;
    }
    if (existing.bullionBattle?.name) {
        return existing.bullionBattle;
    }
    const battle: LeagueBullionBattle = {
        name: leagueBullionBattleName(existing),
        entryStake: LEAGUE_BULLION_BATTLE_STAKE,
        ensuredAt: new Date().toISOString(),
    };
    updateLeague(leagueId, (league) => ({ ...league, bullionBattle: battle }));
    return battle;
}

/**
 * Prototype: mark the viewer as entered in the shared Bullion battle and open a
 * league play for the entry stake (idempotent if already entered).
 * Pass picks to persist the board for the demo played-view reopen.
 * Returns `{ ok: true }` or `{ ok: false, error }` when coins are short.
 */
export function enterLeagueBullionBattle(
    leagueId: string,
    input: {
        userId: number;
        playerName: string;
        selections?: Record<string, unknown>;
        bonusPicks?: unknown[];
    },
): { ok: true } | { ok: false; error: string } {
    const league = findLeague(leagueId);
    if (!league) return { ok: false, error: "League not found." };
    if (!leagueIncludedGames(league).includes("bullion")) {
        return { ok: false, error: "Bullion is not in this league." };
    }

    const battle = ensureLeagueBullionBattle(leagueId) ?? league.bullionBattle;
    if (!battle) return { ok: false, error: "Battle not ready." };

    const alreadyIn =
        battle.enteredUserIds?.includes(input.userId) ||
        league.plays.some((play) => play.game === "bullion" && play.userId === input.userId);

    const stake = Number.isFinite(Number(battle.entryStake))
        ? Math.max(0, Math.round(Number(battle.entryStake)))
        : LEAGUE_BULLION_BATTLE_STAKE;
    if (!alreadyIn && stake > 0) {
        const available = availableCoinsFor(league, input.userId);
        if (available < stake) {
            return { ok: false, error: "Not enough league balls for this battle." };
        }
    }

    const now = Date.now();
    const play: LeaguePlay | null = alreadyIn
        ? null
        : {
              id: `play-${now}-${input.userId}-bullion`,
              userId: input.userId,
              playerName: input.playerName,
              game: "bullion",
              stake,
              selection: battle.name,
              multiplier: 3,
              placedAt: new Date(now).toISOString(),
              settlesAt: new Date(now + LEAGUE_PLAY_SETTLE_DELAY_MS).toISOString(),
              status: "open",
          };

    const hasPicks = input.selections != null && Object.keys(input.selections).length > 0;

    updateLeague(leagueId, (row) => {
        const current = row.bullionBattle ?? battle;
        const enteredUserIds = current.enteredUserIds?.includes(input.userId)
            ? current.enteredUserIds
            : [...(current.enteredUserIds ?? []), input.userId];
        const viewerPicksByUserId = { ...(current.viewerPicksByUserId ?? {}) };
        const bonusPicks = Array.isArray(input.bonusPicks) ? input.bonusPicks : [];
        if (hasPicks) {
            viewerPicksByUserId[String(input.userId)] = {
                selections: input.selections!,
                bonusPicks,
            };
        }
        const viewerSlip = hasPicks
            ? { picks: input.selections!, bonusPicks }
            : current.viewerSlip;
        return {
            ...row,
            bullionBattle: {
                ...current,
                enteredUserIds,
                viewerPicksByUserId,
                viewerEntered: true,
                ...(viewerSlip ? { viewerSlip } : {}),
            },
            plays: play ? [play, ...row.plays] : row.plays,
        };
    });
    return { ok: true };
}

export function inviteFriendsToLeague(leagueId: string, userIds: number[]): void {
    updateLeague(leagueId, (league) => {
        const existing = new Set(league.invitedUserIds);
        league.members.forEach((member) => existing.add(member.userId));
        (league.declinedUserIds ?? []).forEach((id) => existing.add(id));
        const added = userIds.filter((id) => !existing.has(id));
        if (added.length === 0) return league;
        const invitedMembers = [...(league.invitedMembers ?? [])];
        for (const userId of added) {
            const friend = friendById(userId);
            invitedMembers.push({
                userId,
                playerName: friend?.playerName ?? `Player ${userId}`,
                avatar: friend?.avatar ?? null,
                xpWeekCrown: friend?.xpWeekCrown ?? null,
            });
        }
        return {
            ...league,
            invitedUserIds: [...league.invitedUserIds, ...added],
            invitedMembers,
        };
    });
}

/**
 * Prototype stand-in for a friend accepting: moves an invited friend into the
 * table so the host can see the effect of an invite without a second browser.
 */
export function simulateInviteAccepted(leagueId: string, userId: number): void {
    updateLeague(leagueId, (league) => {
        if (!league.invitedUserIds.includes(userId)) return league;
        const friend = friendById(userId);
        const entryBump = Math.max(0, Math.round(league.entryFeeRubies)) * PRIZE_COINS_PER_RUBY;
        return {
            ...league,
            prizePotCoins: leaguePrizePotCoins(league) + entryBump,
            invitedUserIds: league.invitedUserIds.filter((id) => id !== userId),
            invitedMembers: league.invitedMembers?.filter((member) => member.userId !== userId),
            declinedUserIds: league.declinedUserIds?.filter((id) => id !== userId),
            declinedMembers: league.declinedMembers?.filter((member) => member.userId !== userId),
            members: [
                ...league.members,
                newMember({
                    userId,
                    playerName: friend?.playerName ?? `Player ${userId}`,
                    avatar: friend?.avatar ?? null,
                    xpWeekCrown: friend?.xpWeekCrown ?? null,
                    isOnline: friend?.isOnline ?? false,
                    entryFeeRubies: league.entryFeeRubies,
                    formatOvers: leagueFormatOvers(league),
                }),
            ],
        };
    });
}

/** The viewer paying the entry fee and taking their starting coins. */
export function joinLeague(
    leagueId: string,
    viewer: { name: string; avatar: string | null; xpWeekCrown?: string | null },
): boolean {
    const target = findLeague(leagueId);
    if (!target) return false;
    if (target.members.some((member) => member.isViewer)) return false;
    updateLeague(leagueId, (league) => {
        const entryBump = Math.max(0, Math.round(league.entryFeeRubies)) * PRIZE_COINS_PER_RUBY;
        return {
            ...league,
            prizePotCoins: leaguePrizePotCoins(league) + entryBump,
            membership: "member",
            invitedUserIds: league.invitedUserIds.filter((id) => id !== LEAGUE_VIEWER_USER_ID),
            invitedMembers: league.invitedMembers?.filter((member) => member.userId !== LEAGUE_VIEWER_USER_ID),
            declinedUserIds: league.declinedUserIds?.filter((id) => id !== LEAGUE_VIEWER_USER_ID),
            declinedMembers: league.declinedMembers?.filter((member) => member.userId !== LEAGUE_VIEWER_USER_ID),
            members: [
                ...league.members,
                newMember({
                    userId: LEAGUE_VIEWER_USER_ID,
                    playerName: viewer.name,
                    avatar: viewer.avatar,
                    xpWeekCrown: viewer.xpWeekCrown ?? null,
                    isViewer: true,
                    entryFeeRubies: league.entryFeeRubies,
                    formatOvers: leagueFormatOvers(league),
                }),
            ],
        };
    });
    return true;
}

/**
 * Prototype seat top-up: credit chips + bump startingCoins (profit-neutral) + grow pot.
 * Does not debit real rubies in demo mode.
 */
export function topUpLeagueSeat(
    leagueId: string,
    userId: number = LEAGUE_VIEWER_USER_ID,
): {
    ok: boolean;
    error?: string;
} {
    const target = findLeague(leagueId);
    if (!target) return { ok: false, error: "League not found." };
    if (
        isLeagueRefunded(target) ||
        leagueStatus(target) === "ended" ||
        (target.scope === "match" && Boolean(target.matchFinishedAt))
    ) {
        return { ok: false, error: "This league is no longer open for top-ups." };
    }
    const member = target.members.find((row) => row.userId === userId);
    if (!member) return { ok: false, error: "You have not joined this league." };
    if (memberSeatTopups(member) >= seatTopUpMax(leagueFormatOvers(target))) {
        return { ok: false, error: "You have used all seat top-ups for this league." };
    }
    const topUpAtOrBelow = leagueSeatTopUpAvailableAtOrBelow(target);
    const counting = seatChipsCountingTowardTopUp(target, userId);
    if (topUpAtOrBelow <= 0 || counting > topUpAtOrBelow) {
        return {
            ok: false,
            error: `You can still play every game here — top up once you have ${formatCoins(topUpAtOrBelow)} free league balls or fewer.`,
        };
    }
    const chips = seatTopUpChips(leagueFormatOvers(target));
    const potBump = SEAT_TOPUP_RUBIES * PRIZE_COINS_PER_RUBY;
    const seatTopupsAfter = memberSeatTopups(member) + 1;
    const playerName = member.playerName?.trim() || `Player ${userId}`;
    updateLeague(leagueId, (league) => ({
        ...league,
        prizePotCoins: leaguePrizePotCoins(league) + potBump,
        members: league.members.map((row) => {
            if (row.userId !== userId) return row;
            return {
                ...row,
                coins: row.coins + chips,
                startingCoins: row.startingCoins + chips,
                seatTopups: seatTopupsAfter,
            };
        }),
        plays: league.plays.map((play) => {
            if (play.userId !== userId || play.game !== "squares" || play.status !== "open") return play;
            const current = Math.max(0, Math.floor(Number(play.stake) || 0));
            if (current <= 0) return play;
            const previous = Math.max(0, Math.floor(Number(play.marketDetails?.stakeFloor) || 0));
            const stakeFloor = Math.max(previous, current);
            return {
                ...play,
                marketDetails: { ...(play.marketDetails ?? {}), stakeFloor },
            };
        }),
        seatTopUps: [
            {
                id: `seat-topup-${leagueId}-${userId}-${seatTopupsAfter}`,
                at: new Date().toISOString(),
                userId,
                playerName,
                chips,
                potBump,
                rubies: SEAT_TOPUP_RUBIES,
                seatTopupsAfter,
            },
            ...(league.seatTopUps ?? []),
        ],
    }));
    return { ok: true };
}

export function declineInvite(leagueId: string): void {
    updateLeague(leagueId, (league) => {
        if (league.membership !== "invited") return league;
        const declined = league.invitedMembers?.find((member) => member.userId === LEAGUE_VIEWER_USER_ID);
        return {
            ...league,
            membership: "none",
            invitedUserIds: league.invitedUserIds.filter((id) => id !== LEAGUE_VIEWER_USER_ID),
            invitedMembers: league.invitedMembers?.filter((member) => member.userId !== LEAGUE_VIEWER_USER_ID),
            declinedUserIds: [
                ...(league.declinedUserIds ?? []).filter((id) => id !== LEAGUE_VIEWER_USER_ID),
                LEAGUE_VIEWER_USER_ID,
            ],
            declinedMembers: [
                ...(league.declinedMembers ?? []).filter((member) => member.userId !== LEAGUE_VIEWER_USER_ID),
                ...(declined
                    ? [declined]
                    : [
                          {
                              userId: LEAGUE_VIEWER_USER_ID,
                              playerName: "You",
                              avatar: null,
                          },
                      ]),
            ],
        };
    });
}

/**
 * Demo: bump sent/received tallies and prepend an Activity throw row.
 */
export function recordPrototypeLeagueReaction(input: {
    leagueId: string;
    reactionId?: string;
    fromUserId: number;
    targetUserId: number;
    emoji: import("./leagueReactions").LeagueReactionEmoji;
}): void {
    const { leagueId, fromUserId, targetUserId, emoji } = input;
    if (!leagueId || fromUserId === targetUserId) return;
    updateLeague(leagueId, (league) => {
        const event = leagueReactionActivityFromThrow({
            reactionId: input.reactionId?.trim() || `proto-throw-${Date.now()}-${fromUserId}-${targetUserId}`,
            members: league.members,
            fromUserId,
            targetUserId,
            emoji,
        });
        return {
            ...league,
            members: applyLeagueReactionToMembers(league.members, { fromUserId, targetUserId, emoji }),
            reactionEvents: event
                ? prependLeagueReactionEvent(league.reactionEvents, event)
                : league.reactionEvents,
        };
    });
    if (fromUserId === LEAGUE_VIEWER_USER_ID && typeof window !== "undefined") {
        window.setTimeout(() => {
            void import("./leagueReactions").then((mod) => {
                const latest = findLeague(leagueId);
                if (!latest) return;
                mod.simulateIncomingLeagueReaction(leagueId, latest.members, { targetUserId: fromUserId });
            });
        }, 1200);
    }
}

function pick<T>(items: readonly T[]): T {
    return items[Math.floor(Math.random() * items.length)];
}

/**
 * Apply a wallet coin delta that originated from a real game page in League mode.
 * Spends open a held play; positive returns settle that play for points without refilling chips.
 */
export function applyLeagueWalletDelta(
    leagueId: string,
    input: {
        userId: number;
        playerName: string;
        game: LeagueGameKey;
        delta: number;
        selection?: string;
    },
): void {
    if (!Number.isFinite(input.delta) || input.delta === 0) return;
    if (input.delta < 0) {
        placePlay(leagueId, {
            userId: input.userId,
            playerName: input.playerName,
            game: input.game,
            stake: Math.round(Math.abs(input.delta)),
            selection: input.selection ?? `${LEAGUE_GAME_META[input.game].label} play`,
            multiplier: 2,
            settleDelayMs: 120000,
        });
        return;
    }

    updateLeague(leagueId, (league) => {
        const play = league.plays.find(
            (row) => row.status === "open" && row.userId === input.userId && row.game === input.game,
        );
        // Orphan credits used to refill seat chips — ignore them. Chips only come from ruby top-ups.
        if (!play) return league;
        return settlePlayWithReturn(league, play.id, Math.round(input.delta));
    });
}

function settlePlayWithReturn(
    league: League,
    playId: string,
    returnedCoins: number,
    marketDetailsPatch?: LeaguePlay["marketDetails"],
): League {
    const play = league.plays.find((row) => row.id === playId && row.status === "open");
    if (!play) return league;

    // Squares points model: any scored return is a win. Other games still need to beat stake.
    const won = play.game === "squares" ? returnedCoins > 0 : returnedCoins > play.stake;
    const delta = returnedCoins - play.stake;
    // Spend the held stake; scoring return never refills chips.
    const seatDelta = -play.stake;
    const ranksBefore = new Map(leagueTableRows(league).map((row) => [row.userId, row.rank]));
    const nextMarketDetails =
        marketDetailsPatch != null
            ? { ...(play.marketDetails ?? {}), ...marketDetailsPatch }
            : play.marketDetails;

    const members = league.members.map((member) => {
        if (member.userId !== play.userId) return member;
        const stats = member.byGame[play.game] ?? { pl: 0, wins: 0, losses: 0, played: 0 };
        return {
            ...member,
            coins: member.coins + seatDelta,
            byGame: {
                ...member.byGame,
                [play.game]: {
                    pl: stats.pl + delta,
                    wins: stats.wins + (won ? 1 : 0),
                    losses: stats.losses + (won ? 0 : 1),
                    played: stats.played + 1,
                },
            },
        } as LeagueMember;
    });

    const settlement: LeagueSettlement = {
        id: `settle-${play.id}`,
        at: new Date().toISOString(),
        userId: play.userId,
        playerName: play.playerName,
        game: play.game,
        outcome: won ? "win" : "loss",
        stake: play.stake,
        delta,
        detail: `${play.selection} · ${won ? `scored ${returnedCoins.toLocaleString()}` : "lost"}`,
        ...(nextMarketDetails ? { marketDetails: nextMarketDetails } : {}),
    };

    const settled: League = {
        ...league,
        members,
        plays: league.plays.map((row) =>
            row.id === play.id
                ? {
                      ...row,
                      status: "settled" as const,
                      outcome: won ? "win" : "loss",
                      delta,
                      ...(nextMarketDetails ? { marketDetails: nextMarketDetails } : {}),
                  }
                : row,
        ),
        settlements: [settlement, ...league.settlements],
    };
    return markRankChanges(settled, ranksBefore);
}

/** Settle an open play by absolute chips scored as points (used by Super Squares demo). */
export function settleOpenPlayWithReturn(
    leagueId: string,
    playId: string,
    returnedCoins: number,
    marketDetailsPatch?: LeaguePlay["marketDetails"],
): void {
    updateLeague(leagueId, (league) =>
        settlePlayWithReturn(league, playId, Math.max(0, Math.round(returnedCoins)), marketDetailsPatch),
    );
}

/** Places a play. Coins are not debited until settlement — the stake is held open.
 * Squares: replacing picks on the same open over updates stake + grid in place. */
export function placePlay(
    leagueId: string,
    input: {
        userId: number;
        playerName: string;
        game: LeagueGameKey;
        stake: number;
        selection: string;
        multiplier: number;
        settleDelayMs?: number;
        marketDetails?: LeaguePlay["marketDetails"];
    },
): void {
    updateLeague(leagueId, (league) => {
        if (!leagueIncludedGames(league).includes(input.game)) return league;

        if (input.game === "squares" && input.marketDetails?.overNumber != null) {
            const overNum = Number(input.marketDetails.overNumber);
            const innsNum =
                input.marketDetails.inningsNumber != null
                    ? Number(input.marketDetails.inningsNumber)
                    : 1;
            const existing = league.plays.find(
                (row) =>
                    row.status === "open" &&
                    row.game === "squares" &&
                    row.userId === input.userId &&
                    Number(row.marketDetails?.overNumber) === overNum &&
                    Number(row.marketDetails?.inningsNumber ?? 1) === innsNum,
            );
            if (existing) {
                const available =
                    availableCoinsFor(league, input.userId) + Math.max(0, Number(existing.stake) || 0);
                if (input.stake > available) return league;
                const stakeFloor = Math.max(
                    0,
                    Math.floor(Number(existing.marketDetails?.stakeFloor) || 0),
                );
                if (input.stake < stakeFloor) return league;
                return {
                    ...league,
                    plays: league.plays.map((row) =>
                        row.id === existing.id
                            ? {
                                  ...row,
                                  stake: input.stake,
                                  selection: input.selection,
                                  multiplier: input.multiplier,
                                  marketDetails: {
                                      ...input.marketDetails,
                                      ...(stakeFloor > 0 ? { stakeFloor } : {}),
                                  },
                              }
                            : row,
                    ),
                };
            }
        }

        const now = Date.now();
        const available = availableCoinsFor(league, input.userId);
        if (input.stake > available) return league;
        const play: LeaguePlay = {
            id: `play-${now}-${input.userId}-${Math.floor(Math.random() * 10000)}`,
            userId: input.userId,
            playerName: input.playerName,
            game: input.game,
            stake: input.stake,
            selection: input.selection,
            multiplier: input.multiplier,
            placedAt: new Date(now).toISOString(),
            settlesAt: new Date(now + (input.settleDelayMs ?? LEAGUE_PLAY_SETTLE_DELAY_MS)).toISOString(),
            status: "open",
            ...(input.marketDetails ? { marketDetails: input.marketDetails } : {}),
        };
        return { ...league, plays: [play, ...league.plays] };
    });
}

/** First Sixes reveal in a prototype league — Activity + My Games card. Idempotent. */
export function recordPrototypeSixesCardIssued(
    leagueId: string,
    viewer: { userId: number; name: string },
): void {
    const league = findLeague(leagueId);
    if (!league) return;
    const already = league.plays.some((play) => play.game === "sixes" && play.userId === viewer.userId);
    if (already) return;
    placePlay(leagueId, {
        userId: viewer.userId,
        playerName: viewer.name,
        game: "sixes",
        stake: 0,
        selection: "One card",
        multiplier: 1,
        settleDelayMs: 8 * 3600 * 1000,
        marketDetails: { cardScore: 0 },
    });
}

/** Records who actually changed position, so the movement arrows survive a few settlements. */
function markRankChanges(league: League, before: Map<number, number>): League {
    const movedAt = new Date().toISOString();
    const after = new Map(leagueTableRows(league).map((row) => [row.userId, row.rank]));
    return {
        ...league,
        members: league.members.map((member) => {
            const previous = before.get(member.userId);
            const current = after.get(member.userId);
            if (previous == null || current == null || previous === current) return member;
            return { ...member, previousRank: previous, rankMovedAt: movedAt };
        }),
    };
}

/** Longer odds win less often. Seeded viewer plays lean win-y so the walkthrough shows a toast. */
function didPlayWin(multiplier: number, play: LeaguePlay): boolean {
    if (play.id.startsWith("seed-play-") && play.userId === LEAGUE_VIEWER_USER_ID) {
        return Math.random() < 0.78;
    }
    const chance = Math.min(0.7, Math.max(0.12, 0.98 / multiplier));
    return Math.random() < chance;
}

function applyPlaySettlement(league: League, playId: string): League {
    const play = league.plays.find((row) => row.id === playId && row.status === "open");
    if (!play) return league;

    const won = didPlayWin(play.multiplier, play);
    const returned = won ? Math.round(play.stake * play.multiplier) : 0;
    const delta = returned - play.stake;
    const seatDelta = -play.stake;
    const ranksBefore = new Map(leagueTableRows(league).map((row) => [row.userId, row.rank]));

    const members = league.members.map((member) => {
        if (member.userId !== play.userId) return member;
        const stats = member.byGame[play.game] ?? { pl: 0, wins: 0, losses: 0, played: 0 };
        return {
            ...member,
            coins: member.coins + seatDelta,
            byGame: {
                ...member.byGame,
                [play.game]: {
                    pl: stats.pl + delta,
                    wins: stats.wins + (won ? 1 : 0),
                    losses: stats.losses + (won ? 0 : 1),
                    played: stats.played + 1,
                },
            },
        } as LeagueMember;
    });

    const settlement: LeagueSettlement = {
        id: `settle-${play.id}`,
        at: new Date().toISOString(),
        userId: play.userId,
        playerName: play.playerName,
        game: play.game,
        outcome: won ? "win" : "loss",
        stake: play.stake,
        delta,
        detail: `${play.selection} · ${won ? `scored ${returned.toLocaleString()}` : "lost"}`,
        ...(play.marketDetails ? { marketDetails: play.marketDetails } : {}),
    };

    const settled: League = {
        ...league,
        members,
        plays: league.plays.map((row) =>
            row.id === play.id ? { ...row, status: "settled", outcome: won ? "win" : "loss", delta } : row,
        ),
        settlements: [settlement, ...league.settlements],
    };
    return markRankChanges(settled, ranksBefore);
}

/** Bots keep the in-play list and the feed alive without the viewer doing anything. */
function addBotPlay(league: League): League {
    const openByBots = league.plays.filter((play) => play.status === "open" && !isViewerId(league, play.userId));
    if (openByBots.length >= LEAGUE_MAX_OPEN_BOT_PLAYS) return league;

    const candidates = league.members.filter(
        (member) => !member.isViewer && availableCoinsFor(league, member.userId) >= LEAGUE_PLAY_STAKE_MIN,
    );
    if (candidates.length === 0) return league;
    const member = pick(candidates);
    const available = availableCoinsFor(league, member.userId);
    const affordable = LEAGUE_PLAY_STAKE_PRESETS.filter((stake) => stake <= available);

    const options = allPlayOptions(league);
    if (options.length === 0) return league;

    const option = pick(options);
    const now = Date.now();
    const play: LeaguePlay = {
        id: `play-${now}-${member.userId}-${Math.floor(Math.random() * 10000)}`,
        userId: member.userId,
        playerName: member.playerName,
        game: option.game,
        stake: affordable.length > 0 ? pick(affordable) : LEAGUE_PLAY_STAKE_MIN,
        selection: option.label,
        multiplier: option.multiplier,
        placedAt: new Date(now).toISOString(),
        settlesAt: new Date(
            now +
                LEAGUE_BOT_PLAY_SETTLE_MIN_MS +
                Math.random() * (LEAGUE_BOT_PLAY_SETTLE_MAX_MS - LEAGUE_BOT_PLAY_SETTLE_MIN_MS),
        ).toISOString(),
        status: "open",
    };
    return { ...league, plays: [play, ...league.plays] };
}

function isViewerId(league: League, userId: number): boolean {
    return league.members.some((member) => member.userId === userId && member.isViewer);
}

function advanceLeague(league: League, now: number): League {
    let next = league;
    const due = next.plays.filter((play) => {
        if (play.status !== "open" || !play.settlesAt) return false;
        return new Date(play.settlesAt).getTime() <= now;
    });
    due.forEach((play) => {
        next = applyPlaySettlement(next, play.id);
    });
    const botChance = league.id === WALKTHROUGH_LIVE_LEAGUE_ID ? 0.12 : LEAGUE_BOT_PLAY_CHANCE;
    if (Math.random() < botChance) {
        next = addBotPlay(next);
    }
    return next;
}

/** Prototype control: settle whichever open play is closest to settling. */
export function settleNow(leagueId: string): void {
    updateLeague(leagueId, (league) => {
        const open = league.plays
            .filter((play) => play.status === "open")
            .sort((a, b) => {
                const aAt = a.settlesAt ? new Date(a.settlesAt).getTime() : Number.POSITIVE_INFINITY;
                const bAt = b.settlesAt ? new Date(b.settlesAt).getTime() : Number.POSITIVE_INFINITY;
                return aAt - bAt;
            });
        if (open.length === 0) {
            const seeded = addBotPlay(league);
            const first = seeded.plays.find((play) => play.status === "open");
            return first ? applyPlaySettlement(seeded, first.id) : league;
        }
        return applyPlaySettlement(league, open[0].id);
    });
}

function tick(): void {
    if (!state.simulating) return;
    const now = Date.now();
    let changed = false;
    const leagues = state.leagues.map((league) => {
        if (league.membership !== "host" && league.membership !== "member") return league;
        if (leagueStatus(league, now) !== "live") return league;
        if (isLeagueRefunded(league, now)) return league;
        const next = advanceLeague(league, now);
        if (next !== league) changed = true;
        return next;
    });
    if (!changed) return;
    commit({ ...state, leagues });
}

function shouldRunTimer(): boolean {
    if (listeners.size === 0 || !state.simulating) return false;
    return state.leagues.some(
        (league) =>
            (league.membership === "host" || league.membership === "member") &&
            leagueStatus(league) === "live" &&
            !isLeagueRefunded(league),
    );
}

function syncTimer(): void {
    if (typeof window === "undefined") return;
    const wanted = shouldRunTimer();
    if (wanted && timerId == null) {
        timerId = setInterval(tick, LEAGUE_SIMULATION_TICK_MS);
        return;
    }
    if (!wanted && timerId != null) {
        clearInterval(timerId);
        timerId = null;
    }
}

/** Exported for the create flow preview: coins each player receives on join. */
export function previewStartingCoins(entryFeeRubies: number): number {
    return startingCoinsFor(entryFeeRubies);
}
